import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { conns, connsPublic } from '@cfg/axiosConfig.tsx';
import { RootState } from '@store/store';
import { message } from 'antd';
import { logError, logInfo } from '@tools/LogsHandler.tsx';
import { Staff, UserProfile } from '@app-types/user.types.ts';
export type { UserProfile } from '@app-types/user.types.ts';
import { Address } from '@eshop/types/location.types.ts';
import { GuestAddress, GuestInitResponse } from '@eshop/types/guest.types.ts';
import { login, logout, checkSession } from './authSlice';

const GUEST_SESSION_KEY = 'eshop_guest_session';
const DEFAULT_GUEST_SECRET = 'df_av_guest_secret';

export const CATALOGUE_EDIT_LAYOUT_PERMISSION = 'catalog:edit_layout';

const EMPTY_ROLES: string[] = [];
Object.freeze(EMPTY_ROLES);

const ADDRESS_FIELD_LABELS: Record<string, string> = {
  first_name: 'el nombre',
  last_name: 'el apellido',
  street: 'la dirección',
  city: 'la localidad',
  state: 'la provincia',
  postcode: 'el código postal',
  phone_number: 'el celular',
};

const getAddressErrorMessage = (error: any, fallback: string): string => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const field = Array.isArray(item?.loc) ? String(item.loc[item.loc.length - 1] ?? '') : '';
        const label = ADDRESS_FIELD_LABELS[field];
        if (label && String(item?.msg ?? '').toLowerCase().includes('required')) {
          return `Completá ${label}.`;
        }
        return item?.msg ? String(item.msg) : '';
      })
      .filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
};

interface StoredGuestSession {
  email?: string;
  phone?: string;
  token?: string;
  verifiedAt?: string;
}

interface PersistedGuestSession {
  version?: number;
  token?: string;
  email?: string; // legacy plain-text
  phone?: string; // legacy plain-text
  emailEnc?: string;
  phoneEnc?: string;
  verifiedAtEnc?: string;
}

const getBuffer = (): typeof Buffer | undefined => {
  if (typeof globalThis === 'undefined') {
    return undefined;
  }
  return (globalThis as { Buffer?: typeof Buffer }).Buffer;
};

const toBase64 = (bytes: Uint8Array): string => {
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return window.btoa(binary);
  }

  const BufferCtor = getBuffer();
  if (BufferCtor) {
    return BufferCtor.from(bytes).toString('base64');
  }
  throw new Error('Base64 encoding is not supported in this environment');
};

const fromBase64 = (value: string): Uint8Array => {
  if (typeof window !== 'undefined' && typeof window.atob === 'function') {
    const binary = window.atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  const BufferCtor = getBuffer();
  if (BufferCtor) {
    return new Uint8Array(BufferCtor.from(value, 'base64'));
  }
  throw new Error('Base64 decoding is not supported in this environment');
};

const xorBuffer = (bytes: Uint8Array, key: string): Uint8Array => {
  const keyBytes = new TextEncoder().encode(key || DEFAULT_GUEST_SECRET);
  const result = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) {
    result[i] = bytes[i] ^ keyBytes[i % keyBytes.length];
  }
  return result;
};

const encryptGuestField = (value: string, token?: string): string => {
  const valueBytes = new TextEncoder().encode(value);
  const encrypted = xorBuffer(valueBytes, token ?? DEFAULT_GUEST_SECRET);
  return toBase64(encrypted);
};

const tryDecryptGuestField = (payload: string, key: string): string | null => {
  try {
    const decoded = fromBase64(payload);
    const decrypted = xorBuffer(decoded, key);
    return new TextDecoder().decode(decrypted);
  } catch {
    return null;
  }
};

const decryptGuestField = (payload: string, token?: string): string | undefined => {
  const primaryKey = token ?? DEFAULT_GUEST_SECRET;
  const primary = tryDecryptGuestField(payload, primaryKey);
  if (primary !== null) {
    return primary;
  }
  if (primaryKey === DEFAULT_GUEST_SECRET) {
    return undefined;
  }
  const fallback = tryDecryptGuestField(payload, DEFAULT_GUEST_SECRET);
  return fallback === null ? undefined : fallback;
};

const loadGuestSession = (): StoredGuestSession | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(GUEST_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedGuestSession;
    const token = typeof parsed.token === 'string' ? parsed.token : undefined;
    const email =
      typeof parsed.emailEnc === 'string'
        ? decryptGuestField(parsed.emailEnc, token)
        : typeof parsed.email === 'string'
          ? parsed.email
          : undefined;
    const phone =
      typeof parsed.phoneEnc === 'string'
        ? decryptGuestField(parsed.phoneEnc, token)
        : typeof parsed.phone === 'string'
          ? parsed.phone
          : undefined;
    const verifiedAt =
      typeof parsed.verifiedAtEnc === 'string'
        ? decryptGuestField(parsed.verifiedAtEnc, token)
        : undefined;

    if (!token && !email && !phone) {
      return null;
    }

    const session: StoredGuestSession = {};
    if (token) session.token = token;
    if (email) session.email = email;
    if (phone) session.phone = phone;
    if (verifiedAt) session.verifiedAt = verifiedAt;
    return session;
  } catch {
    return null;
  }
};

const persistGuestSession = (session: StoredGuestSession | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (!session || (!session.email && !session.phone && !session.token)) {
      window.localStorage.removeItem(GUEST_SESSION_KEY);
      return;
    }

    const payload: PersistedGuestSession = { version: 2 };
    if (session.token) {
      payload.token = session.token;
    }
    if (session.email) {
      payload.emailEnc = encryptGuestField(session.email, session.token);
    }
    if (session.phone) {
      payload.phoneEnc = encryptGuestField(session.phone, session.token);
    }
    if (session.verifiedAt) {
      payload.verifiedAtEnc = encryptGuestField(session.verifiedAt, session.token);
    }

    window.localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(payload));
  } catch {
    /* noop */
  }
};

const storedGuest = loadGuestSession();

const mapGuestAddressToAddress = (guest: GuestAddress): Address => ({
  ...guest.address,
  guestAddressId: guest.id,
});

const addressesToGuest = (addresses: Address[]): GuestAddress[] =>
  addresses
    .filter((addr) => (addr as { guestAddressId?: number }).guestAddressId !== undefined)
    .map((addr) => ({
      id: Number(addr.guestAddressId),
      label: (addr as { label?: string }).label,
      address: addr,
    }));

/* ───────────────── Estado ───────────────── */

interface StaffNames {
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}

export type ContactChannel = 'email' | 'phone';

interface ContactVerificationRequestPayload {
  channel: ContactChannel;
  value: string;
}

interface ContactVerificationResponse {
  status: string;
  channel: ContactChannel;
  masked_target: string;
  expires_at: string;
}

interface ContactVerificationError {
  code?: string;
  message?: string;
  retry_after?: number;
  remaining_attempts?: number;
  field?: string;
}

interface ContactVerificationState {
  status: 'idle' | 'requesting' | 'awaiting' | 'verifying' | 'error';
  channel: ContactChannel | null;
  maskedTarget?: string;
  expiresAt?: string;
  remainingAttempts?: number | null;
  retryAfter?: number | null;
  error?: string | null;
}

interface UserState {
  profile: UserProfile | null;
  staffList: Staff[];
  names: Record<string, StaffNames>;
  /** Lista de direcciones del usuario */
  addresses: Address[];
  guestAddresses: GuestAddress[];
  guestToken: string | null;
  guestPhoneVerifiedAt: string | null;
  guestCheckoutRecovery?: { token: string; email: string; phone: string; expiresAt: number } | null;
  status: 'idle' | 'loading' | 'failed';
  isStaff: boolean;
  isSuperuser: boolean;
  permissions: string[];
  contactVerification: ContactVerificationState;
}

/* ───────────────── Permisos y roles ───────────────── */

/**
 * Mapa de alias de permisos/grupos a un permiso canónico.
 * Ej: si en el backend viene "eshop:catalogue:editor" o "catalogue:editor",
 * todos terminan convirtiéndose en "catalog:edit_layout".
 *
 * Esto permite que el backend use distintas etiquetas/roles
 * y el frontend las unifique a un mismo permiso interno.
 */
const GROUP_PERMISSION_ALIASES: Record<string, readonly string[]> = {
  [CATALOGUE_EDIT_LAYOUT_PERMISSION]: [CATALOGUE_EDIT_LAYOUT_PERMISSION],
  'catalogue:edit_layout': [CATALOGUE_EDIT_LAYOUT_PERMISSION],
  'catalogue:editor': [CATALOGUE_EDIT_LAYOUT_PERMISSION],
  'catalogue_editor': [CATALOGUE_EDIT_LAYOUT_PERMISSION],
  'catalogue editor': [CATALOGUE_EDIT_LAYOUT_PERMISSION],
  'catalog:editor': [CATALOGUE_EDIT_LAYOUT_PERMISSION],
  'eshop.catalogue.editor': [CATALOGUE_EDIT_LAYOUT_PERMISSION],
  'eshop:catalogue:editor': [CATALOGUE_EDIT_LAYOUT_PERMISSION],
};

/**
 * Normaliza cualquier token de permiso/rol:
 *  - si no es string -> null
 *  - trim y toLowerCase
 */
const normaliseToken = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.toLowerCase() : null;
};

/**
 * Agrega un "token" de permiso a un Set, aplicando:
 *  - normalización del string
 *  - mapeo de alias a permisos canónicos (GROUP_PERMISSION_ALIASES)
 */
const addPermissionToken = (target: Set<string>, token: unknown) => {
  const normalised = normaliseToken(token);
  if (!normalised) {
    return;
  }
  const mapped = GROUP_PERMISSION_ALIASES[normalised];
  // Si el token es un alias, se agregan los permisos canónicos asociados
  if (mapped) {
    mapped.forEach((permission) => target.add(permission));
    return;
  }
  // Si no hay alias, se agrega el token normalizado tal cual
  target.add(normalised);
};

/**
 * Punto central donde se "leen" los permisos que vienen del backend dentro del UserProfile.
 *
 * Soporta dos formatos:
 *  - profile.permissions: string[]
 *  - profile.permissions: { [perm: string]: boolean }
 *
 * Además, también toma profile.groups y los procesa como permisos/alias
 * (esto permite que grupos del backend activen permisos en el frontend).
 */
const collectPermissionsFromProfile = (profile: UserProfile | null): string[] => {
  if (!profile) {
    return [];
  }

  const derived = new Set<string>();
  const rawPermissions = (profile as { permissions?: unknown }).permissions;

  // Formato 1: array de strings -> cada entrada es un permiso o alias
  if (Array.isArray(rawPermissions)) {
    rawPermissions.forEach((entry) => addPermissionToken(derived, entry));
  }
  // Formato 2: objeto { "perm_x": true, "perm_y": false }
  else if (rawPermissions && typeof rawPermissions === 'object') {
    Object.entries(rawPermissions).forEach(([key, enabled]) => {
      if (enabled) {
        addPermissionToken(derived, key);
      }
    });
  }

  // También se interpretan los grupos como alias de permisos.
  // Ej: un group "eshop:catalogue:editor" puede mapearse a "catalog:edit_layout".
  (profile.groups ?? []).forEach((group) => addPermissionToken(derived, group));

  // Se devuelve un array plano de permisos normalizados y sin duplicados
  return Array.from(derived);
};


/**
 * Lee el campo profile.role (array de strings) y lo normaliza a un Set.
 * Se usa para derivar flags como isStaff / isSuperuser.
 */
const collectRoleSet = (profile: UserProfile | null): Set<string> => {
  const roles = new Set<string>();
  (profile?.role ?? []).forEach((role) => {
    const normalised = normaliseToken(role);
    if (normalised) {
      roles.add(normalised);
    }
  });
  return roles;
};


/**
 * Función central que actualiza el estado de usuario cuando llega
 * un UserProfile nuevo desde el backend:
 *
 *  - guarda el perfil completo
 *  - deriva isStaff / isSuperuser a partir de profile.role
 *  - recalcula el array de permissions usando collectPermissionsFromProfile
 *
 * Esta función se llama en:
 *  - login.fulfilled
 *  - checkSession.fulfilled
 *  - fetchProfile.fulfilled
 *  - updateProfile.fulfilled
 *  - updateUserBasicInfoAsync.fulfilled
 *  - confirmContactVerification.fulfilled
 *  - y también para el modo guest (guestProfile)
 */
const applyProfile = (state: UserState, profile: UserProfile | null): void => {
  state.profile = profile;
  const roles = collectRoleSet(profile);
  state.isStaff = roles.has('staff') || roles.has('superuser');
  state.isSuperuser = roles.has('superuser');
  state.permissions = collectPermissionsFromProfile(profile);
};

const buildGuestProfile = (guest: StoredGuestSession | null): UserProfile | null => {
  if (!guest) {
    return null;
  }

  return {
    id: 0,
    username: 'guest',
    email: guest.email ?? '',
    first_name: '',
    last_name: '',
    phone: guest.phone ?? '',
    role: [],
    groups: [],
    permissions: [],
  };
};

const contactVerificationInitialState: ContactVerificationState = {
  status: 'idle',
  channel: null,
  maskedTarget: undefined,
  expiresAt: undefined,
  remainingAttempts: null,
  retryAfter: null,
  error: null,
};

/* ───────────────── Estado inicial ───────────────── */

const initialGuestProfile = buildGuestProfile(storedGuest);

const initialState: UserState = {
  profile: initialGuestProfile,
  staffList: [],
  names: {},
  addresses: [],
  guestAddresses: [],
  guestToken: storedGuest?.token ?? null,
  guestPhoneVerifiedAt: storedGuest?.verifiedAt ?? null,
  status: 'idle',
  isStaff: false,
  isSuperuser: false,
  // Para guest se calculan permisos en base al perfil invitado (normalmente vacío)
  permissions: collectPermissionsFromProfile(initialGuestProfile),
  contactVerification: { ...contactVerificationInitialState },
};

/* ──────────────── Thunks ───────────────────*/
export const fetchProfile = createAsyncThunk(
  'user/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await conns('user:me').get();

      logInfo('fetchProfile - profile: ', data);
      return data as UserProfile;
    } catch (e: unknown) {
      logError('fetchProfile - error: ', e);
      return rejectWithValue('No se pudo obtener el perfil');
    }
  },
);

export const updateProfile = createAsyncThunk<
  UserProfile,
  Partial<UserProfile>,
  { rejectValue: string }
>(
  'user/updateProfile',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await conns('user:update').put(payload);
      message.success('Perfil actualizado 👍');
      return data as UserProfile;
    } catch (e: unknown) {
      logError('updateProfile - error: ', e);
      return rejectWithValue(
        (e as any)?.response?.data?.detail || 'Error al actualizar el perfil'
      );
    }
  },
);



export const fetchStaffList = createAsyncThunk<
  Staff[],
  void,
  { state: RootState; rejectValue: string }
>('user/fetchStaffList', async (_, { getState, rejectWithValue }) => {
  const { auth } = getState();

  if (!auth.isAuthenticated) {
    return rejectWithValue('Usuario no autenticado');
  }

  try {
    logInfo('--- fetchStaffList ---');
    const { data } = await conns<Staff[]>('user:staff_list').get();
    logInfo('fetchStaffList - staff count:', Array.isArray(data) ? data.length : 0);
    return data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    logError('fetchStaffList - error:', e);
    return rejectWithValue(e?.response?.data?.detail || 'Error al cargar staff');
  }
});

/* ──────────────── Direcciones ───────────────────*/
export const fetchAddresses = createAsyncThunk<
  Address[],
  void,
  { state: RootState; rejectValue: string }
>('user/fetchAddresses', async (_, { getState, rejectWithValue }) => {
  const { auth, user } = getState();

  if (auth.isAuthenticated) {
    try {
      const { data } = await conns<Address[]>('user:address').get();
      logInfo('fetchAddresses - loaded count:', Array.isArray(data) ? data.length : 0);
      return data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      logError('fetchAddresses - error:', e);
      return rejectWithValue(e?.response?.data?.detail || 'Error al obtener direcciones');
    }
  }

  if (!user.guestToken) {
    return rejectWithValue('Usuario no autenticado');
  }

  try {
    const { data } = await connsPublic<GuestAddress[]>('eshop:checkout:guest:addresses').get(
      {},
      { headers: { 'X-Guest-Token': user.guestToken } },
    );
    logInfo('fetchGuestAddresses - loaded count:', Array.isArray(data) ? data.length : 0);
    return data.map(mapGuestAddressToAddress);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    logError('fetchGuestAddresses - error:', e);
    return rejectWithValue(e?.response?.data?.detail || 'Error al obtener direcciones');
  }
});

export const editAddress = createAsyncThunk<
  Address,
  Address,
  { state: RootState; rejectValue: string }
>('user/editAddress', async (addr, { getState, rejectWithValue }) => {
  const { auth, user } = getState();

  if (auth.isAuthenticated) {
    try {
      const response = addr.id
        ? await conns<Address>('user:address', [addr.id]).put(addr)
        : await conns<Address>('user:address').post(addr);

      logInfo('editAddress - saved id:', response.data?.id ?? 'new');
      message.success('Dirección guardada 👍');
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      logError('editAddress - error:', e);
      return rejectWithValue(getAddressErrorMessage(e, 'No se pudo guardar la dirección. Revisá los datos e intentá nuevamente.'));
    }
  }

  if (!user.guestToken) {
    return rejectWithValue('Usuario no autenticado');
  }

  const guestAddressId = (addr as { guestAddressId?: number }).guestAddressId;
  const addressPayload = Object.fromEntries(
    Object.entries(addr).filter(([key]) =>
      ['first_name', 'last_name', 'street', 'addr_detail', 'city', 'state', 'postcode', 'country', 'notes', 'phone_number'].includes(key),
    ),
  );

  try {
    const response = guestAddressId
      ? await connsPublic<GuestAddress>('eshop:checkout:guest:addresses', [guestAddressId]).put(
        { address: addressPayload },
        { headers: { 'X-Guest-Token': user.guestToken } },
      )
      : await connsPublic<GuestAddress>('eshop:checkout:guest:addresses').post(
        { address: addressPayload },
        { headers: { 'X-Guest-Token': user.guestToken } },
      );

    logInfo('editGuestAddress - saved id:', response.data?.id ?? 'new');
    message.success('Dirección guardada 👍');
    return mapGuestAddressToAddress(response.data);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    logError('editGuestAddress - error:', e);
    return rejectWithValue(getAddressErrorMessage(e, 'No se pudo guardar la dirección. Revisá los datos e intentá nuevamente.'));
  }
});

export const deleteAddress = createAsyncThunk<
  number,
  number,
  { state: RootState; rejectValue: string }
>('user/deleteAddress', async (addressId, { getState, rejectWithValue }) => {
  const { auth } = getState();

  if (!auth.isAuthenticated) {
    return rejectWithValue('Usuario no autenticado');
  }

  try {
    await conns('user:address', [addressId]).delete();
    message.success('Dirección eliminada 👍');
    return addressId;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    logError('deleteAddress - error:', e);
    return rejectWithValue(e?.response?.data?.detail || 'Error al eliminar dirección');
  }
});

export const requestContactVerification = createAsyncThunk<
  ContactVerificationResponse,
  ContactVerificationRequestPayload,
  { rejectValue: ContactVerificationError }
>(
  'user/requestContactVerification',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await conns<ContactVerificationResponse>('user:contact_request').post(payload);
      return data;
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        const detailCode = (detail as { code?: unknown }).code;
        return rejectWithValue({
          ...(detail as Record<string, unknown>),
          message:
            (detail as { message?: string }).message ??
            (detailCode === 'otp_delivery_failed'
              ? 'No se pudo enviar el código porque el servicio de mensajería no está disponible. Intentá nuevamente más tarde.'
              : 'No se pudo enviar el código de verificación'),
        });
      }
      logError('requestContactVerification - error:', e);
      return rejectWithValue({ message: 'No se pudo enviar el código de verificación' });
    }
  },
);

export const confirmContactVerification = createAsyncThunk<
  UserProfile,
  { channel: ContactChannel; code: string },
  { rejectValue: ContactVerificationError }
>(
  'user/confirmContactVerification',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await conns<UserProfile>('user:contact_confirm').post(payload);
      message.success('Contacto verificado 👍');
      return data;
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        return rejectWithValue({
          ...(detail as Record<string, unknown>),
          message:
            (detail as { message?: string }).message ?? 'No se pudo validar el código ingresado',
        });
      }
      logError('confirmContactVerification - error:', e);
      return rejectWithValue({ message: 'No se pudo validar el código ingresado' });
    }
  },
);

export const updateUserBasicInfoAsync = createAsyncThunk<
  UserProfile,
  { phone: string },
  { state: RootState; rejectValue: string }
>('user/updateBasicInfo', async (payload, { getState, rejectWithValue }) => {
  const { auth } = getState();

  if (!auth.isAuthenticated) {
    return rejectWithValue('Usuario no autenticado');
  }

  try {
    const { data } = await conns<UserProfile>('user:update').put({ phone: payload.phone });
    message.success('Información actualizada 👍');
    return data;
  } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    logError('updateUserBasicInfoAsync - error:', e);
    return rejectWithValue(e?.response?.data?.detail || 'Error al actualizar información');
  }
});

export const initGuestSession = createAsyncThunk<
  GuestInitResponse,
  { email: string; phone: string; fallback_token?: string },
  { rejectValue: string }
>('user/initGuestSession', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await connsPublic<GuestInitResponse>('eshop:checkout:guest:init').post(payload);
    return data;
  } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    logError('initGuestSession - error:', { status: e?.response?.status, code: e?.response?.data?.detail?.code });
    return rejectWithValue(
      e?.response?.data?.detail?.message ||
      e?.response?.data?.detail ||
      'No se pudo guardar los datos de contacto',
    );
  }
});

/* ──────────────── Slice ───────────────────*/
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearProfile: (s) => {
      applyProfile(s, null);
      s.guestCheckoutRecovery = null;
      s.guestPhoneVerifiedAt = null;
    },
    setAnonymousUserBasicInfo: (s, a) => {
      if (s.profile && s.profile.id && s.profile.id !== 0) {
        return;
      }
      const email = a.payload.email ?? s.profile?.email ?? '';
      const phone =
        a.payload.phone ?? (a.payload as { tel?: string }).tel ?? s.profile?.phone ?? '';
      applyProfile(s, {
        id: 0,
        username: 'guest',
        email,
        first_name: '',
        last_name: '',
        phone,
        role: [],
        groups: [],
        permissions: [],
      });
      persistGuestSession({
        email,
        phone,
        token: s.guestToken ?? undefined,
        verifiedAt: s.guestPhoneVerifiedAt ?? undefined,
      });
    },
    setGuestAddresses: (s, a: PayloadAction<GuestAddress[]>) => {
      s.guestAddresses = a.payload;
      if (!s.isStaff && s.guestToken) {
        s.addresses = a.payload.map(mapGuestAddressToAddress);
      }
    },
    resetContactVerification: (s) => {
      s.contactVerification = { ...contactVerificationInitialState };
    },
    markGuestPhoneVerified: (s, a: PayloadAction<string | null>) => {
      s.guestPhoneVerifiedAt = a.payload ?? null;
      s.guestCheckoutRecovery = null;
      const shouldPersist = Boolean(
        s.guestToken || s.profile?.email || s.profile?.phone,
      );
      if (shouldPersist) {
        persistGuestSession({
          email: s.profile?.email ?? undefined,
          phone: s.profile?.phone ?? undefined,
          token: s.guestToken ?? undefined,
          verifiedAt: s.guestPhoneVerifiedAt ?? undefined,
        });
      }
    },
  },
  extraReducers: (b) => {
    /* Login/Logout */
    b.addCase(login.fulfilled, (s, a) => {
      s.guestCheckoutRecovery = null;
      applyProfile(s, a.payload);
      s.guestToken = null;
      s.guestAddresses = [];
      s.guestPhoneVerifiedAt = null;
      persistGuestSession(null);
      s.contactVerification = { ...contactVerificationInitialState };
    });
    b.addCase(checkSession.fulfilled, (s, a) => {
      s.guestCheckoutRecovery = null;
      applyProfile(s, a.payload);
      s.guestToken = null;
      s.guestAddresses = [];
      s.guestPhoneVerifiedAt = null;
      persistGuestSession(null);
      s.contactVerification = { ...contactVerificationInitialState };
    });
    b.addCase(checkSession.rejected, (s, action) => {
      if (action.payload?.code !== 'unauthenticated') {
        return;
      }
      const guest = loadGuestSession();
      const guestProfile = buildGuestProfile(guest);
      applyProfile(s, guestProfile);
      s.guestToken = guest?.token ?? null;
      s.guestPhoneVerifiedAt = guest?.verifiedAt ?? null;
      s.contactVerification = { ...contactVerificationInitialState };
    });
    b.addCase(logout.fulfilled, (s) => {
      s.guestCheckoutRecovery = null;
      const guest = loadGuestSession();
      const guestProfile = buildGuestProfile(guest);
      applyProfile(s, guestProfile);
      s.guestToken = guest?.token ?? null;
      s.guestPhoneVerifiedAt = guest?.verifiedAt ?? null;
      s.staffList = [];
      s.names = {};
      s.addresses = [];
      s.guestAddresses = [];
      s.contactVerification = { ...contactVerificationInitialState };
    });
    /* Perfil */
    b.addCase(fetchProfile.pending, (s) => {
      s.status = 'loading';
    });
    b.addCase(fetchProfile.fulfilled, (s, a) => {
      s.status = 'idle';
      applyProfile(s, a.payload);
    });
    b.addCase(fetchProfile.rejected, (s) => {
      s.status = 'failed';
      applyProfile(s, null);
    });

    /* Actualizar perfil */
    b.addCase(updateProfile.pending, (s) => {
      s.status = 'loading';
    });
    b.addCase(updateProfile.fulfilled, (s, a) => {
      s.status = 'idle';
      applyProfile(s, a.payload);
    });
    b.addCase(updateProfile.rejected, (s) => {
      s.status = 'failed';
    });

    /* Actualizar info básica (tel/email) */
    b.addCase(updateUserBasicInfoAsync.pending, (s) => {
      s.status = 'loading';
    });
    b.addCase(updateUserBasicInfoAsync.fulfilled, (s, a) => {
      s.status = 'idle';
      applyProfile(s, a.payload);
    });
    b.addCase(updateUserBasicInfoAsync.rejected, (s) => {
      s.status = 'failed';
    });

    b.addCase(initGuestSession.pending, (s) => {
      s.status = 'loading';
    });
    b.addCase(initGuestSession.fulfilled, (s, a) => {
      s.status = 'idle';
      const guestProfile = a.payload.profile;
      applyProfile(s, {
        id: 0,
        username: 'guest',
        email: guestProfile.email,
        first_name: '',
        last_name: '',
        phone: guestProfile.phone,
        role: [],
        groups: [],
        permissions: [],
      });
      s.guestToken = guestProfile.guest_token;
      s.guestCheckoutRecovery = a.meta.arg.fallback_token ? {
        token: a.meta.arg.fallback_token, email: guestProfile.email, phone: guestProfile.phone, expiresAt: Date.now() + 1800000,
      } : null;
      s.guestAddresses = a.payload.addresses;
      s.addresses = a.payload.addresses.map(mapGuestAddressToAddress);
      const verifiedAt = guestProfile.phone_verified_at ?? guestProfile.last_verified_at;
      s.guestPhoneVerifiedAt = verifiedAt ?? null;
      persistGuestSession({
        email: guestProfile.email,
        phone: guestProfile.phone,
        token: guestProfile.guest_token,
        verifiedAt: verifiedAt ?? undefined,
      });
    });
    b.addCase(initGuestSession.rejected, (s) => {
      s.status = 'failed';
    });

    /* Staff */
    b.addCase(fetchStaffList.pending, (s) => {
      s.status = 'loading';
    });
    b.addCase(fetchStaffList.fulfilled, (s, a) => {
      s.status = 'idle';
      s.staffList = a.payload;
      s.names = a.payload.reduce<Record<string, StaffNames>>((acc, u) => {
        acc[u.id.toString()] = {
          username: u.username,
          email: u.email,
          first_name: u.first_name,
          last_name: u.last_name,
        };
        return acc;
      }, {});
    });
    b.addCase(fetchStaffList.rejected, (s) => {
      s.status = 'failed';
    });

    /* Direcciones */
    b.addCase(fetchAddresses.pending, (s) => {
      s.status = 'loading';
    });
    b.addCase(fetchAddresses.fulfilled, (s, a) => {
      s.status = 'idle';
      s.addresses = a.payload;
      if (!s.isStaff && s.guestToken) {
        s.guestAddresses = addressesToGuest(a.payload);
      }
    });
    b.addCase(fetchAddresses.rejected, (s) => {
      s.status = 'failed';
    });

    b.addCase(editAddress.pending, (s) => {
      s.status = 'loading';
    });
    b.addCase(editAddress.fulfilled, (s, a) => {
      s.status = 'idle';

      const idx = s.addresses.findIndex((d) => d.id === a.payload.id);
      if (idx >= 0) {
        s.addresses[idx] = a.payload;
      } else {
        s.addresses.push(a.payload);
      }
      if (!s.isStaff && s.guestToken) {
        s.guestAddresses = addressesToGuest(s.addresses);
      }
    });
    b.addCase(editAddress.rejected, (s) => {
      s.status = 'failed';
    });

    b.addCase(deleteAddress.pending, (s) => {
      s.status = 'loading';
    });
    b.addCase(deleteAddress.fulfilled, (s, a) => {
      s.status = 'idle';
      s.addresses = s.addresses.filter((addr) => addr.id !== a.payload);
      if (!s.isStaff && s.guestToken) {
        s.guestAddresses = addressesToGuest(s.addresses);
      }
    });
    b.addCase(deleteAddress.rejected, (s) => {
      s.status = 'failed';
    });

    /* Verificación de contacto */
    b.addCase(requestContactVerification.pending, (s, action) => {
      s.contactVerification.status = 'requesting';
      s.contactVerification.channel = action.meta.arg.channel;
      s.contactVerification.maskedTarget = undefined;
      s.contactVerification.expiresAt = undefined;
      s.contactVerification.error = null;
      s.contactVerification.retryAfter = null;
      s.contactVerification.remainingAttempts = null;
    });
    b.addCase(requestContactVerification.fulfilled, (s, action) => {
      s.contactVerification.status = 'awaiting';
      s.contactVerification.channel = action.payload.channel;
      s.contactVerification.maskedTarget = action.payload.masked_target;
      s.contactVerification.expiresAt = action.payload.expires_at;
      s.contactVerification.error = null;
      s.contactVerification.retryAfter = null;
      s.contactVerification.remainingAttempts = null;
    });
    b.addCase(requestContactVerification.rejected, (s, action) => {
      s.contactVerification.status = 'error';
      s.contactVerification.error = action.payload?.message ?? 'No se pudo enviar el código de verificación';
      s.contactVerification.maskedTarget = undefined;
      s.contactVerification.expiresAt = undefined;
      s.contactVerification.retryAfter =
        typeof action.payload?.retry_after === 'number' ? action.payload.retry_after : null;
      s.contactVerification.remainingAttempts =
        typeof action.payload?.remaining_attempts === 'number'
          ? action.payload.remaining_attempts
          : null;
    });

    b.addCase(confirmContactVerification.pending, (s) => {
      s.contactVerification.status = 'verifying';
      s.contactVerification.error = null;
    });
    b.addCase(confirmContactVerification.fulfilled, (s, action) => {
      s.contactVerification = { ...contactVerificationInitialState };
      applyProfile(s, action.payload);
    });
    b.addCase(confirmContactVerification.rejected, (s, action) => {
      s.contactVerification.status = 'error';
      s.contactVerification.error = action.payload?.message ?? 'No se pudo validar el código ingresado';
      if (typeof action.payload?.remaining_attempts === 'number') {
        s.contactVerification.remainingAttempts = action.payload.remaining_attempts;
      }
      if (typeof action.payload?.retry_after === 'number') {
        s.contactVerification.retryAfter = action.payload.retry_after;
      }
    });
  },
});

export const {
  clearProfile,
  setAnonymousUserBasicInfo,
  setGuestAddresses,
  resetContactVerification,
  markGuestPhoneVerified,
} = userSlice.actions;
export const __userSliceTestUtils = {
  loadGuestSession,
  persistGuestSession,
  encryptGuestField,
  decryptGuestField,
};
export const selectProfile = (s: RootState) => s?.user?.profile;
export const selectProfileStatus = (s: RootState) => s?.user?.status ?? 'idle';
export const selectAddresses = (s: RootState) => s?.user?.addresses ?? [];
export const selectContactVerification = (s: RootState) =>
  s?.user?.contactVerification ?? contactVerificationInitialState;
export const selectIsStaff = (s: RootState) => s?.user?.isStaff ?? false;
export const selectIsSuperuser = (s: RootState) => s?.user?.isSuperuser ?? false;
export const selectUserPermissions = (s: RootState) => s?.user?.permissions ?? [];
export const selectUserRoles = (s: RootState) => s?.user?.profile?.role ?? EMPTY_ROLES;
export const selectUserGroups = (s: RootState) => s?.user?.profile?.groups ?? EMPTY_ROLES;
const STAFF_ADMIN_GROUPS = new Set([
  'erp:staff:admin',
  'admin',
  'administrator',
  'owner',
  'owners',
  'staff:admin',
  'staff:owner',
  'staff:owners',
  'erp:owner',
  'erp:owners',
]);
export const selectIsStaffAdmin = (s: RootState) => (
  selectIsSuperuser(s)
  || selectUserGroups(s).some((group) => (
    typeof group === 'string' && STAFF_ADMIN_GROUPS.has(group.trim().toLowerCase())
  ))
);
export default userSlice.reducer;
